import { ReactPortal } from '../../../components/reactPortal'
import { Modal } from '../../../components/modal/modal'
import { useSelector, useDispatch } from '../../../hooks/redux'
import { CustomButton } from '../../../components/customButton'
import { TextField } from '../../../components/textField/textField'
import { CustomIcon } from '../../../components/customIcon/customIcon'
import { Checkbox } from '../../../components/checkbox/checkbox'
import { OutClick } from '../../../components/outClick'
import ttsClasses from '../../../styles/tts/ttsModal.module.scss'
import classes from '../../../styles/admin/adminModal.module.scss'
import listClasses from '../../../styles/admin/usersList.module.scss'
import {
  setModalVisibility,
  setDefaultModalSettings,
  updateModalSettings,
} from '../../../store/slices/admin/adminSlice'
import cn from 'classnames'
import { TModalUser } from '../../../@types/admin'
import { createUser } from '../../../store/slices/user/async'
import { getVoiceGroups } from '../../../store/slices/voiceGroups/async'
import { useEffect, useState } from 'react'
import { AutocompleteOffFields } from '../../../components/textField/autocompleteOffFields'

export const AdminModal = () => {
  const dispatch = useDispatch()
  const modal = useSelector((state) => state.admin.modal)
  const auth_token = useSelector((state) => state.user.user.auth_token)
  const groups = useSelector((state) => state.voiceGroups.groups)
  const [error, setError] = useState(false)
  const [voiceGroupOpen, setVoiceGroupOpen] = useState(false)
  const [selectedVoiceGroups, setSelectedVoiceGroups] = useState<number[]>([])

  const isOpen = modal.type === 'user'
  const hasTts = modal.user.access_tts
  const voiceGroupsDisabled = !hasTts

  const systemGroups = [...groups.filter((g) => g.is_system)].reverse()
  const customGroups = groups.filter((g) => !g.is_system)

  useEffect(() => {
    if (isOpen) {
      if (!groups.length) {
        dispatch(getVoiceGroups() as any).then((action: any) => {
          const loadedGroups: { voice_group_id: number; is_default: boolean }[] = action.payload ?? []
          const defaultGroup = loadedGroups.find((g) => g.is_default)
          if (defaultGroup) setSelectedVoiceGroups([defaultGroup.voice_group_id])
        })
      } else {
        const defaultGroup = groups.find((g) => g.is_default)
        if (defaultGroup) setSelectedVoiceGroups([defaultGroup.voice_group_id])
      }
    }
  }, [isOpen])

  const defaultGroupId = groups.find((g) => g.is_default)?.voice_group_id

  const toggleVoiceGroup = (id: number) => {
    setSelectedVoiceGroups((prev) => {
      const next = prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
      if (next.length === 0 && defaultGroupId !== undefined) return [defaultGroupId]
      return next
    })
  }

  const voiceGroupLabel = (() => {
    if (selectedVoiceGroups.length === 0) return 'Группы голосов'
    if (selectedVoiceGroups.length === groups.length) return 'Все группы'
    if (selectedVoiceGroups.length === 1) {
      return groups.find((g) => g.voice_group_id === selectedVoiceGroups[0])?.name ?? 'Группы голосов'
    }
    return `${selectedVoiceGroups.length} группы`
  })()

  const handleCloseModal = () => {
    dispatch(setModalVisibility('none'))
    dispatch(setDefaultModalSettings())
    setError(false)
    setSelectedVoiceGroups([])
  }

  const handleChangeProperty = (property: Partial<TModalUser>) => {
    dispatch(updateModalSettings(property))
  }

  const addUser = async (event: any) => {
    event.preventDefault()
    try {
      if (modal.user.auth_username && !(hasTts && selectedVoiceGroups.length === 0)) {
        const { auth_token: token, auth_username: name, ...rest } = modal.user

        const payload = { token, name, auth_token, ...rest, voice_group_ids: selectedVoiceGroups }

        await dispatch(createUser(payload) as any)
        handleCloseModal()
      } else {
        setError(true)
      }
    } catch (e) {
      // dispatch(addNotify({ text: `Не удалось добавить пользователя (${e})` }))
    }
  }

  useEffect(() => {
    if (error && modal.user.auth_username) {
      setError(false)
    }
  }, [modal])

  return (
    <ReactPortal>
      <Modal
        classes={{ modalContent: cn(ttsClasses.ttsModal, ttsClasses.TtsModalSettings) }}
        title="Новый пользователь"
        isOpen={modal.type === 'user'}
        handleClose={handleCloseModal}
      >
        <div className={classes.newUserModalContent}>
          <AutocompleteOffFields />

          <TextField
            localeText="Имя пользователя"
            value={modal.user.auth_username}
            error={error}
            type="text"
            handleChangeInput={(e) => handleChangeProperty({ auth_username: e.target.value })}
          />
          <TextField
            localeText="Токен"
            value={modal.user.auth_token}
            type="password"
            handleChangeInput={(e) => handleChangeProperty({ auth_token: e.target.value.replace(/[^\x00-\x7F]/g, '') })}
          />
          <OutClick
            className={classes.modalSelect}
            onClick={voiceGroupOpen ? () => setVoiceGroupOpen(false) : () => null}
          >
            <>
              <div
                className={cn(classes.voiceGroupTrigger, voiceGroupsDisabled && classes.voiceGroupTriggerDisabled)}
                onClick={() => !voiceGroupsDisabled && setVoiceGroupOpen((p) => !p)}
              >
                <span>{voiceGroupLabel}</span>
                <CustomIcon icon={voiceGroupOpen ? 'arrowDown' : 'arrowRight'} />
              </div>
              {voiceGroupOpen && (
                <div className={classes.voiceGroupDropdown}>
                  {systemGroups.map((group) => {
                    const icon = group.voice_group_id === 69 ? 'crown' : 'ttsLogo'
                    const iconClass = icon === 'crown' ? classes.voiceGroupIconSmall : classes.voiceGroupIconMedium
                    return (
                      <div
                        key={group.voice_group_id}
                        className={cn(
                          classes.voiceGroupItem,
                          selectedVoiceGroups.includes(group.voice_group_id) && classes.voiceGroupItemSelected
                        )}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          toggleVoiceGroup(group.voice_group_id)
                        }}
                      >
                        <Checkbox
                          isChecked={selectedVoiceGroups.includes(group.voice_group_id)}
                          handleCheckboxChange={() => {}}
                        />
                        <span className={iconClass}>
                          <CustomIcon icon={icon} />
                        </span>
                        <span className={cn(classes.voiceGroupItemName, classes.voiceGroupItemNameSystem)}>
                          {group.name}
                        </span>
                        <span className={classes.voiceGroupItemCount}>
                          {group.voices.length}{' '}
                          {group.voices.length === 1 ? 'голос' : group.voices.length < 5 ? 'голоса' : 'голосов'}
                        </span>
                      </div>
                    )
                  })}
                  {systemGroups.length > 0 && <div className={classes.voiceGroupDivider} />}
                  {customGroups.length > 0 && (
                    <>
                      {customGroups.map((group) => (
                        <div
                          key={group.voice_group_id}
                          className={cn(
                            classes.voiceGroupItem,
                            selectedVoiceGroups.includes(group.voice_group_id) && classes.voiceGroupItemSelected
                          )}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            toggleVoiceGroup(group.voice_group_id)
                          }}
                        >
                          <Checkbox
                            isChecked={selectedVoiceGroups.includes(group.voice_group_id)}
                            handleCheckboxChange={() => {}}
                          />
                          <span className={classes.voiceGroupItemName}>{group.name}</span>
                          <span className={classes.voiceGroupItemCount}>
                            {group.voices.length}{' '}
                            {group.voices.length === 1 ? 'голос' : group.voices.length < 5 ? 'голоса' : 'голосов'}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </>
          </OutClick>
          <div className={classes.modalUserAccesses}>
            <CustomButton
              onClick={() => handleChangeProperty({ access_asr: !modal.user.access_asr })}
              className={cn(
                listClasses.accessButton,
                modal.user.access_asr ? listClasses.active : listClasses.disabled
              )}
            >
              ASR
            </CustomButton>
            <CustomButton
              onClick={() => handleChangeProperty({ access_tts: !modal.user.access_tts })}
              className={cn(
                listClasses.accessButton,
                modal.user.access_tts ? listClasses.active : listClasses.disabled
              )}
            >
              TTS
            </CustomButton>
            <CustomButton
              onClick={() => handleChangeProperty({ access_console: !modal.user.access_console })}
              className={cn(
                listClasses.accessButton,
                modal.user.access_console ? listClasses.active : listClasses.disabled
              )}
            >
              Консоль
            </CustomButton>
            <CustomButton
              onClick={() => handleChangeProperty({ access_admin: !modal.user.access_admin })}
              className={cn(
                listClasses.accessButton,
                modal.user.access_admin ? listClasses.active : listClasses.disabled
              )}
            >
              Админка
            </CustomButton>
          </div>
          <div className={classes.newUserModalFooter}>
            <CustomButton onClick={handleCloseModal}>Отмена</CustomButton>
            <CustomButton viewType="primary" onClick={addUser}>
              Добавить
            </CustomButton>
          </div>
        </div>
      </Modal>
    </ReactPortal>
  )
}
